import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MessageSquare, Users, MessagesSquare, Cog, Lock, Plus } from "lucide-react";
import { useStore } from "@/state/legacy_module_api";
import ThreadsNav from "@/features/threads/nav/ThreadsNav";
import ContactsNav from "@/features/contacts/nav/ContactsNav";
import SettingsNav from "@/features/more/nav/SettingsNav";
import { ContactsNavPopover } from "@/features/contacts/nav/ContactsNavPopover";
import { initializeApp, isAppInitialized } from "@/app/module_api";
import { useAppState, useActions } from "@/state/legacy_module_api";
import { Button } from "@/ui/components/ui/button";
import { InitialProfilePicture } from "@/ui/components/InitialProfilePicture";
import ThreadView from "@/features/chat/screens/ThreadView";

type TabId = "messages" | "users" | "communities" | "more";

const RootScreen = () => {
  const isRootPageInit = useStore((s) => s.isRootPageInit);
  const activeTab = useStore((s) => s.activeTab);
  const socketConnected = useStore((s) => s.wsConnected);
  const tokenType = useStore((s) => (s as any).tokenType);
  const setIsRootPageInit = useStore((s) => s.setIsRootPageInit);
  const setActiveTab = useStore((s) => s.setActiveTab);
  const threadCount = useStore((s) => Object.keys((s as any).threadMap || {}).length);
  const userCount = useStore((s) => Object.keys((s as any).contactsById || {}).length);
  // Get current user ID to identify self-chat threads
  const myUserId = useStore((s) => (s as any).myUserID);
  // Boolean unread signals for the nav tabs. Core's model is per-thread boolean
  // (not a count), so we surface a dot, not a number — mirroring the thread-row
  // and community-row dots. Threads tab = any non-community DM unread;
  // Communities tab = any community channel unread.
  const threadsHaveUnread = useStore((s) =>
    Object.entries((s as any).threadMap || {}).some(
      ([threadKey, thread]: [string, any]) => {
        if (!thread.unread) return false;
        if (thread.communityId) return false; // rolls up to Communities tab instead
        // Exclude self-chat threads (Note to Self)
        if (myUserId && threadKey.startsWith("self:")) {
          const selfId = threadKey.replace("self:", "");
          if (selfId === myUserId) return false;
        }
        return true;
      }
    )
  );
  const communitiesHaveUnread = useStore((s) =>
    Object.values((s as any).threadMap || {}).some(
      (thread: any) => thread.communityId && thread.unread
    )
  );
  const [contactsPopoverOpen, setContactsPopoverOpen] = useState(false);
  const hydrationAttemptedRef = useRef(false);
  const setThreadsTabShowCreateGroup = useStore((s) => s.setThreadsTabShowCreateGroup);

  // HiLo lock state
  const { isConfigured } = useAppState().hilo;
  const { activateLockScreen } = useActions().hilo;
  const { myHandle } = useAppState();

  // Derive current tab from activeTab state
  const currentTab: TabId =
    activeTab === "/users-tab"
      ? "users"
      : activeTab === "/communities-tab"
        ? "communities"
      : activeTab === "/more-tab"
          ? "more"
          : "messages";

  useEffect(() => {
    if (!isRootPageInit) {
      setIsRootPageInit(true);
    }
  }, [isRootPageInit, setIsRootPageInit]);

  // Fallback hydration in case init hasn't run yet (or state was lost)
  useEffect(() => {
    if (hydrationAttemptedRef.current) return;
    if (!isAppInitialized() || threadCount === 0 || userCount === 0) {
      hydrationAttemptedRef.current = true;
      initializeApp().catch((err) =>
        console.warn("[RootScreen] fallback initializeApp failed", err)
      );
    }
  }, [threadCount, userCount]);

  const handleTabChange = (tabId: TabId) => {
    if (tabId === "messages") {
      setActiveTab("/threads-tab");
    } else if (tabId === "users") {
      setActiveTab("/users-tab");
    } else if (tabId === "more") {
      setActiveTab("/more-tab");
    } else if (tabId === "communities") {
      setActiveTab("/communities-tab");
    }
  };

  const navItems: { id: TabId; icon: any; label: string }[] = [
    { id: "messages", icon: MessageSquare, label: "Threads" },
    { id: "users", icon: Users, label: "Contacts" },
    { id: "communities", icon: MessagesSquare, label: "Communities" },
    { id: "more", icon: Cog, label: "Settings" },
  ];

  return (
    <div className="flex h-full">
      {/* Left rail navigation */}
      <aside className="w-20 border-r bg-muted/30 flex flex-col items-center py-4 gap-2">
        {navItems.map(({ id, icon: Icon, label }) => {
          const isActive = currentTab === id;
          return (
            <button
              key={id}
              className={`w-14 h-14 rounded-xl relative flex flex-col items-center justify-center gap-1 transition ${
                isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-accent"
              }`}
              onClick={() => handleTabChange(id)}
            >
              <Icon className="h-5 w-5" />
              <span className="text-[11px] leading-none">{label}</span>
              {((id === "messages" && threadsHaveUnread) ||
                (id === "communities" && communitiesHaveUnread)) && (
                <span
                  data-name="tab-unread-dot"
                  className="absolute top-0 right-1 w-2.5 h-2.5 rounded-full bg-destructive shadow-sm"
                />
              )}
            </button>
          );
        })}

        {/* Lock button - shown when codes are configured */}
        {isConfigured && (
          <>
            <div className="flex-1" />
            <button
              className="w-14 h-14 rounded-xl flex flex-col items-center justify-center gap-1 transition text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
              onClick={() => activateLockScreen()}
            >
              <Lock className="h-5 w-5" />
              <span className="text-[11px] leading-none">Lock</span>
            </button>
          </>
        )}
      </aside>

      <div className="flex-1 flex flex-col h-full">
        {/* Header */}
        <header className="flex items-center justify-between px-4 py-3 border-b bg-background">
          {currentTab !== "more" && (
            <>
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium">
                  {socketConnected ? (
                    <span className="text-green-600">Online</span>
                  ) : (
                    <span className="text-muted-foreground">Offline</span>
                  )}
                </span>
                {myHandle && (
                  <span className="text-xs text-muted-foreground font-mono">
                    {myHandle}
                  </span>
                )}
              </div>

              <div className="flex items-center gap-2">
                {currentTab === "messages" && (
                  <button
                    data-name="create-group-button"
                    className="inline-flex items-center justify-center rounded-full bg-muted p-2 text-foreground shadow-sm transition hover:bg-muted/70 hover:shadow-md"
                    onClick={() => setThreadsTabShowCreateGroup(true)}
                  >
                    <Users className="w-5 h-5" />
                  </button>
                )}
              </div>
            </>
          )}
        </header>

        {/* Tab Content */}
        <main className="flex-1 overflow-auto">
          {currentTab === "messages" && <ThreadsNav />}
          {currentTab === "users" && <ContactsNav />}
          {currentTab === "communities" && <CommunitiesTab />}
          {currentTab === "more" && <SettingsNav />}
        </main>
      </div>

      {/* Popovers */}
      <ContactsNavPopover open={contactsPopoverOpen} onOpenChange={setContactsPopoverOpen} />
    </div>
  );
};

// Communities Tab Component - Two pane layout like ThreadsNav
const CommunitiesTab = () => {
  const communityMap = useStore((s) => s.communityMap);
  const threadMap = useStore((s) => (s as any).threadMap || {});
  const messagesByThread = useStore((s) => (s as any).messagesByThread || {});
  const selectThread = useStore((s) => (s as any).selectThread);
  const userId = useStore((s) => (s as any).userId);
  const navigate = useNavigate();

  // Selection state for two-pane layout
  const [selectedCommunityId, setSelectedCommunityId] = useState<string | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);

  // Create community list from map
  const communityList = Object.values(communityMap).sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  // Get threads that belong to communities
  const communityThreads = Object.values(threadMap).filter(
    (thread: any) => thread.communityId
  ) as any[];

  // Group threads by community
  const threadsByCommunity: Record<string, any[]> = {};
  communityThreads.forEach((thread: any) => {
    if (thread.communityId) {
      if (!threadsByCommunity[thread.communityId]) {
        threadsByCommunity[thread.communityId] = [];
      }
      threadsByCommunity[thread.communityId].push(thread);
    }
  });

  const handleCommunityClick = (community: any) => {
    setSelectedCommunityId(community.id);
    setSelectedGroupId(null);
  };

  const handleChannelClick = (communityId: string, groupId: string) => {
    setSelectedCommunityId(communityId);
    setSelectedGroupId(groupId);
    selectThread?.({
      threadKey: groupId,
      groupId: groupId,
    });
  };

  // Get selected community's channels
  const selectedCommunityChannels = selectedCommunityId
    ? (threadsByCommunity[selectedCommunityId] || []).sort(
        (a: any, b: any) => (b.updatedAt || 0) - (a.updatedAt || 0)
      )
    : [];

  return (
    <div data-name="communities-tab" className="h-full grid grid-cols-[320px_1fr] min-h-0">
      {/* Left sidebar - community list only */}
      <div data-name="communities-sidebar" className="border-r h-full overflow-auto relative">
        {communityList.length === 0 ? (
          <div className="p-4 text-center text-muted-foreground">
            <MessagesSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No communities yet</p>
            <p className="text-sm">Create your first community to get started!</p>
          </div>
        ) : (
          <div className="pb-20">
            {communityList.map((community: any) => {
              const threads = threadsByCommunity[community.id] || [];
              const isSelected = selectedCommunityId === community.id;

              return (
                <div
                  key={community.id}
                  data-name="community-item"
                  data-community-id={community.id}
                  className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-muted/50 border-b ${
                    isSelected ? "bg-muted" : ""
                  }`}
                  onClick={() => handleCommunityClick(community)}
                >
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    {community.image ? (
                      <img
                        src={community.image}
                        alt={community.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <InitialProfilePicture name={community.name} size={40} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold truncate">{community.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {threads.length} channel{threads.length !== 1 ? "s" : ""}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* FAB to create community */}
        <Link to="/communities-create" className="absolute bottom-4 right-4">
          <Button size="icon" className="h-12 w-12 rounded-full shadow-lg">
            <Plus className="h-5 w-5" />
          </Button>
        </Link>
      </div>

      {/* Right panel - channels list or thread view */}
      <div data-name="thread-panel" className="h-full overflow-hidden min-h-0 flex flex-col">
        {selectedGroupId ? (
          <ThreadView
            groupId={selectedGroupId}
            senderId={userId}
            messages={
              messagesByThread[selectedGroupId] ||
              messagesByThread[`group:${selectedGroupId}`] ||
              []
            }
          />
        ) : selectedCommunityId ? (
          <div data-name="community-overview" className="h-full flex flex-col">
            {/* Community header */}
            <div className="px-6 py-4 border-b">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  {communityMap[selectedCommunityId]?.image ? (
                    <img
                      src={communityMap[selectedCommunityId].image}
                      alt={communityMap[selectedCommunityId]?.name}
                      className="w-14 h-14 rounded-full object-cover"
                    />
                  ) : (
                    <InitialProfilePicture
                      name={communityMap[selectedCommunityId]?.name || ""}
                      size={56}
                    />
                  )}
                </div>
                <div>
                  <h2 className="text-xl font-semibold">
                    {communityMap[selectedCommunityId]?.name || "Community"}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {communityMap[selectedCommunityId]?.description || "No description"}
                  </p>
                </div>
              </div>
            </div>

            {/* Channels list */}
            <div className="flex-1 overflow-auto p-4">
              <h3 className="text-sm font-medium text-muted-foreground mb-3 px-2">
                Channels ({selectedCommunityChannels.length})
              </h3>
              {selectedCommunityChannels.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">
                  <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No channels yet</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {selectedCommunityChannels.map((thread: any) => (
                    <div
                      key={thread.id}
                      data-name="community-channel"
                      data-thread-id={thread.id}
                      onClick={() => handleChannelClick(selectedCommunityId, thread.id)}
                      className="flex items-center gap-3 px-3 py-3 rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
                    >
                      <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                        <MessageSquare className="w-5 h-5 text-muted-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate"># {thread.name}</p>
                        {thread.lastMessage && (
                          <p className="text-sm text-muted-foreground truncate">
                            {thread.lastMessage}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div
            data-name="no-selection-placeholder"
            className="h-full flex items-center justify-center text-muted-foreground"
          >
            <div className="text-center">
              <MessagesSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">Communities</p>
              <p className="text-sm">Select a community to view channels</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RootScreen;
